import { useTranslation } from 'react-i18next';

import { SurveyBanner } from './SurveyBanner';
import { useOnboarding } from '../context';

import type { SurveyStepConfig } from '../survey';

/**
 * 닉네임·언어 단계 — 온보딩의 첫 질문(시안 step1 `758:20382`).
 *
 * 닉네임은 자유입력(kind: 'text')이라 옵션 버튼을 그리는 SurveyStep과 렌더가 다르고,
 * 여기서만 언어를 고른다. 언어 응답은 answers에 담지 않는다 — 정본은 i18next의
 * 언어 상태(localStorage `muzly_language`)이고, 답으로 또 적으면 둘이 갈라진다.
 *
 * 배치는 SurveyStep과 같다: 제목 → 배너 → 여백 → **하단 정렬된 필드 카드**. 시안의
 * 필드는 라벨이 카드 **안쪽 위**에 놓이고 값이 그 아래 오는 한 덩어리라, 라벨을 카드
 * 밖에 두면 카드가 두 개가 아니라 네 개로 보인다.
 */

const LANGUAGES = ['ko', 'en'] as const;

const cardClass =
  'flex flex-col gap-1 rounded-2xl border-2 border-[hsl(var(--field-border))] bg-[hsl(var(--surface-lowest))] px-4 py-3.5';
const labelClass =
  'text-sm font-semibold tracking-[-0.07px] text-[hsl(var(--title))]';
const valueClass =
  'rounded-sm bg-transparent text-base font-medium tracking-[-0.08px] text-foreground placeholder:text-[hsl(var(--placeholder))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background';

export const NicknameStep = ({ config }: { config: SurveyStepConfig }) => {
  const { t, i18n } = useTranslation();
  const { answers, answer } = useOnboarding();
  const raw = answers[config.id];
  const nickname = typeof raw === 'string' ? raw : '';

  return (
    <div className="flex flex-1 flex-col">
      <h2 className="whitespace-pre-line text-[22px] font-semibold leading-[1.5] tracking-[-0.11px] text-[hsl(var(--title))]">
        {t(config.titleKey)}
      </h2>

      {config.subtitleKey && (
        <SurveyBanner className="mt-4">{t(config.subtitleKey)}</SurveyBanner>
      )}

      <div className="min-h-6 flex-1" aria-hidden="true" />

      <div className="flex flex-col gap-3">
        {/* 카드가 `<label>`이 아니라 `<div>`인 이유: 시안의 카드는 라벨 + 값 + 설명
            세 줄을 담는데, 그걸 통째로 label로 묶으면 접근성 이름이 세 줄을 이어붙인
            문장이 된다("닉네임 최대 12자까지 쓸 수 있어요"). 라벨은 라벨만 잡고,
            설명은 `aria-describedby`로 잇는다. */}
        <div className={cardClass}>
          <label className={labelClass} htmlFor="onboarding-nickname">
            {t('web.onboarding.nick.label')}
          </label>
          <input
            id="onboarding-nickname"
            type="text"
            value={nickname}
            maxLength={config.maxLength}
            onChange={(e) => answer(config.id, e.target.value)}
            placeholder={
              config.placeholderKey ? t(config.placeholderKey) : undefined
            }
            aria-describedby="onboarding-nickname-hint"
            className={valueClass}
          />
          {/* 길이 상한은 이후 화면 제목이 이 이름을 부르기 때문에 있다 — 왜 잘리는지
              말하지 않으면 12자에서 멈추는 입력이 고장으로 읽힌다. */}
          <span
            id="onboarding-nickname-hint"
            className="text-xs font-medium leading-[18px] text-muted-foreground"
          >
            {t('web.onboarding.nick.hint', { max: config.maxLength })}
          </span>
        </div>

        <div className={cardClass}>
          <label className={labelClass} htmlFor="onboarding-language">
            {t('web.language.label')}
          </label>
          <select
            id="onboarding-language"
            value={i18n.resolvedLanguage ?? i18n.language}
            onChange={(e) => void i18n.changeLanguage(e.target.value)}
            className={valueClass}
          >
            {LANGUAGES.map((lng) => (
              <option key={lng} value={lng}>
                {t(`web.language.${lng}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
